/**
 * Logger Service
 * Centralized logging that automatically disables in production
 * Provides consistent logging interface across the application
 */

const isDev = import.meta.env.DEV;

interface LoggerConfig {
  prefix?: string;
  timestamp?: boolean;
}

class Logger {
  private config: LoggerConfig;

  constructor(config: LoggerConfig = {}) {
    this.config = {
      timestamp: true,
      ...config,
    };
  }

  private formatMessage(level: string, args: unknown[]): unknown[] {
    const parts: unknown[] = [];

    if (this.config.timestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    parts.push(`[${level}]`);
    parts.push(...args);

    return parts;
  }

  /**
   * Log informational messages (only in development)
   */
  log(...args: unknown[]): void {
    if (!isDev) return;
    console.log(...this.formatMessage('INFO', args));
  }

  /**
   * Log error messages (always logged)
   */
  error(...args: unknown[]): void {
    console.error(...this.formatMessage('ERROR', args));
  }

  /**
   * Log warning messages (only in development)
   */
  warn(...args: unknown[]): void {
    if (!isDev) return;
    console.warn(...this.formatMessage('WARN', args));
  }

  /**
   * Log debug messages (only in development)
   */
  debug(...args: unknown[]): void {
    if (!isDev) return;
    console.debug(...this.formatMessage('DEBUG', args));
  }

  /**
   * Log info messages (only in development)
   */
  info(...args: unknown[]): void {
    if (!isDev) return;
    console.info(...this.formatMessage('INFO', args));
  }

  /**
   * Create a grouped log (only in development)
   */
  group(label: string, callback: () => void): void {
    if (!isDev) return;
    console.group(label);
    callback();
    console.groupEnd();
  }

  /**
   * Create a collapsed grouped log (only in development)
   */
  groupCollapsed(label: string, callback: () => void): void {
    if (!isDev) return;
    console.groupCollapsed(label);
    callback();
    console.groupEnd();
  }

  /**
   * Log a table (only in development)
   */
  table(data: unknown): void {
    if (!isDev) return;
    console.table(data);
  }
}

// Default logger instance
export const logger = new Logger();

// Create a logger with a specific prefix
export const createLogger = (prefix: string): Logger => {
  return new Logger({ prefix });
};

// Named loggers for specific modules
export const authLogger = createLogger('AUTH');
export const apiLogger = createLogger('API');
export const storeLogger = createLogger('STORE');
